/**
 * §7.11 One Health spillover heatmap. Live spilloverRiskScore per
 * region; ranked + colored by score.
 */

import { useMemo } from 'react';
import { spilloverRiskScore, ONE_HEALTH_REFERENCES } from '@biomax/plugin-one-health';
import { listRegions } from '@biomax/plugin-region-database';

// Per-region rough proxies (would normally come from a catalog
// adjacent to plugin-region-database; stubbed here as engine-defaults
// + biome-aware estimates).
const REGION_PROXIES: Record<string, {
  wildlifeTradeUsdM: number;
  livestockDensity: number;
  deforestationPct: number;
  batRichness: number;
  climateSuitability: number;
  surveillanceCapacity: number;
}> = {
  cod: { wildlifeTradeUsdM: 800, livestockDensity: 30, deforestationPct: 3.2, batRichness: 120, climateSuitability: 0.85, surveillanceCapacity: 0.25 },
  bra: { wildlifeTradeUsdM: 400, livestockDensity: 250, deforestationPct: 2.5, batRichness: 130, climateSuitability: 0.75, surveillanceCapacity: 0.60 },
  idn: { wildlifeTradeUsdM: 1200, livestockDensity: 90, deforestationPct: 1.8, batRichness: 110, climateSuitability: 0.80, surveillanceCapacity: 0.45 },
  ind: { wildlifeTradeUsdM: 300, livestockDensity: 220, deforestationPct: 0.5, batRichness: 80, climateSuitability: 0.65, surveillanceCapacity: 0.50 },
  chn: { wildlifeTradeUsdM: 1500, livestockDensity: 140, deforestationPct: 0.3, batRichness: 100, climateSuitability: 0.55, surveillanceCapacity: 0.65 },
  vnm: { wildlifeTradeUsdM: 600, livestockDensity: 230, deforestationPct: 0.6, batRichness: 90, climateSuitability: 0.75, surveillanceCapacity: 0.50 },
  phl: { wildlifeTradeUsdM: 500, livestockDensity: 180, deforestationPct: 1.2, batRichness: 90, climateSuitability: 0.80, surveillanceCapacity: 0.45 },
  tha: { wildlifeTradeUsdM: 700, livestockDensity: 200, deforestationPct: 0.4, batRichness: 95, climateSuitability: 0.75, surveillanceCapacity: 0.55 },
  bgd: { wildlifeTradeUsdM: 80, livestockDensity: 400, deforestationPct: 0.2, batRichness: 70, climateSuitability: 0.75, surveillanceCapacity: 0.45 },
  nga: { wildlifeTradeUsdM: 250, livestockDensity: 60, deforestationPct: 1.0, batRichness: 85, climateSuitability: 0.65, surveillanceCapacity: 0.40 },
  ken: { wildlifeTradeUsdM: 180, livestockDensity: 70, deforestationPct: 0.5, batRichness: 75, climateSuitability: 0.60, surveillanceCapacity: 0.50 },
  eth: { wildlifeTradeUsdM: 50, livestockDensity: 120, deforestationPct: 0.9, batRichness: 65, climateSuitability: 0.55, surveillanceCapacity: 0.35 },
  mdg: { wildlifeTradeUsdM: 90, livestockDensity: 40, deforestationPct: 2.8, batRichness: 90, climateSuitability: 0.75, surveillanceCapacity: 0.30 },
  yem: { wildlifeTradeUsdM: 20, livestockDensity: 30, deforestationPct: 0.1, batRichness: 40, climateSuitability: 0.30, surveillanceCapacity: 0.15 },
  zaf: { wildlifeTradeUsdM: 200, livestockDensity: 50, deforestationPct: 0.1, batRichness: 60, climateSuitability: 0.45, surveillanceCapacity: 0.70 },
  egy: { wildlifeTradeUsdM: 80, livestockDensity: 50, deforestationPct: 0.0, batRichness: 30, climateSuitability: 0.25, surveillanceCapacity: 0.60 },
  pak: { wildlifeTradeUsdM: 100, livestockDensity: 200, deforestationPct: 0.4, batRichness: 50, climateSuitability: 0.45, surveillanceCapacity: 0.40 },
  irn: { wildlifeTradeUsdM: 40, livestockDensity: 50, deforestationPct: 0.0, batRichness: 40, climateSuitability: 0.30, surveillanceCapacity: 0.55 },
  usa: { wildlifeTradeUsdM: 100, livestockDensity: 100, deforestationPct: 0.1, batRichness: 50, climateSuitability: 0.50, surveillanceCapacity: 0.85 },
  gbr: { wildlifeTradeUsdM: 50, livestockDensity: 200, deforestationPct: 0.0, batRichness: 20, climateSuitability: 0.35, surveillanceCapacity: 0.85 },
  aus: { wildlifeTradeUsdM: 80, livestockDensity: 20, deforestationPct: 0.3, batRichness: 75, climateSuitability: 0.50, surveillanceCapacity: 0.85 },
  nzl: { wildlifeTradeUsdM: 30, livestockDensity: 200, deforestationPct: 0.0, batRichness: 5, climateSuitability: 0.35, surveillanceCapacity: 0.85 },
  jpn: { wildlifeTradeUsdM: 200, livestockDensity: 70, deforestationPct: 0.0, batRichness: 35, climateSuitability: 0.45, surveillanceCapacity: 0.90 },
  kor: { wildlifeTradeUsdM: 100, livestockDensity: 200, deforestationPct: 0.0, batRichness: 25, climateSuitability: 0.45, surveillanceCapacity: 0.90 },
  sgp: { wildlifeTradeUsdM: 600, livestockDensity: 100, deforestationPct: 0.0, batRichness: 25, climateSuitability: 0.80, surveillanceCapacity: 0.90 },
  deu: { wildlifeTradeUsdM: 100, livestockDensity: 200, deforestationPct: 0.0, batRichness: 25, climateSuitability: 0.35, surveillanceCapacity: 0.85 },
  fra: { wildlifeTradeUsdM: 100, livestockDensity: 150, deforestationPct: 0.0, batRichness: 30, climateSuitability: 0.40, surveillanceCapacity: 0.80 },
  ita: { wildlifeTradeUsdM: 80, livestockDensity: 130, deforestationPct: 0.0, batRichness: 35, climateSuitability: 0.40, surveillanceCapacity: 0.75 },
  esp: { wildlifeTradeUsdM: 80, livestockDensity: 80, deforestationPct: 0.0, batRichness: 30, climateSuitability: 0.40, surveillanceCapacity: 0.80 },
  rus: { wildlifeTradeUsdM: 200, livestockDensity: 10, deforestationPct: 0.2, batRichness: 40, climateSuitability: 0.30, surveillanceCapacity: 0.55 },
  tur: { wildlifeTradeUsdM: 100, livestockDensity: 50, deforestationPct: 0.0, batRichness: 35, climateSuitability: 0.40, surveillanceCapacity: 0.70 },
  sau: { wildlifeTradeUsdM: 50, livestockDensity: 10, deforestationPct: 0.0, batRichness: 20, climateSuitability: 0.20, surveillanceCapacity: 0.70 },
  can: { wildlifeTradeUsdM: 80, livestockDensity: 5, deforestationPct: 0.0, batRichness: 30, climateSuitability: 0.30, surveillanceCapacity: 0.85 },
  mex: { wildlifeTradeUsdM: 150, livestockDensity: 80, deforestationPct: 0.4, batRichness: 80, climateSuitability: 0.60, surveillanceCapacity: 0.55 },
  arg: { wildlifeTradeUsdM: 100, livestockDensity: 30, deforestationPct: 0.4, batRichness: 50, climateSuitability: 0.45, surveillanceCapacity: 0.70 },
};

export function OneHealthHeatmap() {
  const regions = listRegions();

  const scored = useMemo(() => {
    return regions
      .map(r => {
        const px = REGION_PROXIES[r.id] ?? {
          wildlifeTradeUsdM: 100, livestockDensity: 50, deforestationPct: 0.5,
          batRichness: 50, climateSuitability: 0.5, surveillanceCapacity: 0.6,
        };
        const score = spilloverRiskScore({
          regionId: r.id,
          wildlifeTradeVolumeUsdM: px.wildlifeTradeUsdM,
          livestockDensityPerKm2: px.livestockDensity,
          deforestationRatePctPerYr: px.deforestationPct,
          batSpeciesRichness: px.batRichness,
          climateSuitability: px.climateSuitability,
          surveillanceCapacity: px.surveillanceCapacity,
        });
        return { region: r, score };
      })
      .sort((a, b) => b.score.score - a.score.score);
  }, [regions]);

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.11</span>
        <h1 className="surface__title">One Health spillover heatmap</h1>
      </div>
      <div className="surface__sub">
        Per-region risk integrator · {ONE_HEALTH_REFERENCES.filter(r => r.pmid).length} PMID-verified citations · wildlife trade + livestock density + deforestation + bat richness + climate − surveillance capacity
      </div>

      <div className="panel panel--accent" style={{ marginBottom: 'var(--bm-space-4)' }}>
        <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-xs)', color: 'var(--bm-text-muted)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--bm-signal)' }}>ILLUSTRATIVE MODEL INPUTS.</strong> The risk-score *method* is
          PMID-cited, but the per-region driver values (wildlife-trade volume, deforestation rate, bat richness, etc.)
          are rough placeholder estimates, not sourced datasets. v0.3 wires real driver data (UNEP/FAO/IUCN). Scores
          are indicative only.
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Regions assessed</div>
          <div className="kpi__value">{scored.length}</div>
          <div className="kpi__sub">v0.3 catalog</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Top-decile risk</div>
          <div className="kpi__value kpi__value--danger">{scored.filter(s => s.score.score > 0.6).length}</div>
          <div className="kpi__sub">score &gt; 0.60</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">High-surveillance gap</div>
          <div className="kpi__value kpi__value--signal">
            {scored.filter(s => (REGION_PROXIES[s.region.id]?.surveillanceCapacity ?? 1) < 0.4).length}
          </div>
          <div className="kpi__sub">surveillance &lt; 40%</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Spillover risk ranking</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Region</th>
              <th className="num">Score</th>
              <th className="num">Wildlife trade</th>
              <th className="num">Bats</th>
              <th className="num">Deforestation</th>
              <th className="num">Surveillance</th>
            </tr>
          </thead>
          <tbody>
            {scored.map(({ region, score }) => {
              const px = REGION_PROXIES[region.id];
              const colour = score.score > 0.6 ? 'var(--bm-danger)'
                          : score.score > 0.4 ? 'var(--bm-signal)'
                          : 'var(--bm-text-muted)';
              return (
                <tr key={region.id}>
                  <td>{region.names.english}</td>
                  <td className="num" style={{ color: colour, fontWeight: 600 }}>
                    {(score.score * 100).toFixed(0)}
                  </td>
                  <td className="num">${px?.wildlifeTradeUsdM ?? '—'}M</td>
                  <td className="num">{px?.batRichness ?? '—'}</td>
                  <td className="num">{px?.deforestationPct?.toFixed(1) ?? '—'}%</td>
                  <td className="num">{((px?.surveillanceCapacity ?? 0) * 100).toFixed(0)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Verified citations</span>
          <span className="panel__meta">round-tripped via @biomax/plugin-literature-fetch</span>
        </div>
        <table className="dtable">
          <thead>
            <tr><th>ID</th><th>Paper</th><th>PMID</th><th>Backs</th></tr>
          </thead>
          <tbody>
            {ONE_HEALTH_REFERENCES.map(r => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td style={{ fontSize: 12 }}>{r.label}</td>
                <td className="num">{r.pmid ?? '—'}</td>
                <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{r.appliesTo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
